<script lang="ts">
export default {
  name: 'LandingPage',
};
</script>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { History } from '@lucide/vue';
import { useNavigationLinks, iconMap, getIconName } from '@/composables/useNavigationLinks';
import UserFeedbackModal from '@/components/UserFeedbackModal.vue';
import UniversalSearch from '@/components/UniversalSearch.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Store, { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { userProfileService } from '@/services/api';

interface RecentNavLink {
  title: string;
  to?: string;
  icon: string;
  timestamp: number;
}

const router = useRouter();
const showUserFeedbackModal = ref(false);
const recentNavigation = ref<RecentNavLink[]>([]);

const { navigationGroups } = useNavigationLinks();

const hasRecentNavigation = computed(() => recentNavigation.value.length > 0);
const isCurrentHomepage = computed(() => !Store.state.user?.user?.homepagePath || Store.state.user?.user?.homepagePath === '/directory');
const settingHomepage = ref(false);

async function setAsHomepage() {
  settingHomepage.value = true;
  try {
    await userProfileService.updateHomepagePreference({ homepagePath: '/directory' });
    if (Store.state.user?.user) {
      Store.state.user.user.homepagePath = '/directory';
    }
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Homepage updated', color: 'success' });
  } catch {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to update homepage', color: 'error' });
  } finally {
    settingHomepage.value = false;
  }
}

// ----------
// functions
// ----------

function loadRecentNavigation() {
  try {
    const stored = sessionStorage.getItem('cac-recent-navigation');
    if (stored) {
      const parsed = JSON.parse(stored) as RecentNavLink[];
      // Filter out items older than 24 hours and limit to 3
      const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
      recentNavigation.value = parsed.filter((item) => item.timestamp > dayAgo).slice(0, 5);
    }
  } catch (error) {
    console.warn('Failed to load recent navigation from session storage:', error);
    recentNavigation.value = [];
  }
}

function saveRecentNavigation() {
  try {
    sessionStorage.setItem('cac-recent-navigation', JSON.stringify(recentNavigation.value));
  } catch (error) {
    console.warn('Failed to save recent navigation to session storage:', error);
  }
}

function addToRecentNavigation(link: { title: string; to?: string; icon: any }) {
  if (!link.to) return;

  const newItem: RecentNavLink = {
    title: link.title,
    to: link.to,
    icon: typeof link.icon === 'string' ? link.icon : getIconName(link.icon),
    timestamp: Date.now(),
  };

  // Remove existing item with same path if it exists
  const filtered = recentNavigation.value.filter((item) => item.to !== link.to);

  // Add new item to the beginning and limit to 5
  recentNavigation.value = [newItem, ...filtered].slice(0, 5);

  saveRecentNavigation();
}

function handleNavigation(link: any) {
  if (link.to) {
    addToRecentNavigation(link);
    router.push(link.to);
  } else if (link.action) {
    handleAction(link.action);
  }
}

function handleAction(action: string) {
  switch (action) {
    case 'demo':
      window.open('https://demo.platform.cacspecialty.com/', '_blank');
      break;
    case 'feedback':
      showUserFeedbackModal.value = true;
      break;
    case 'help':
      window.open('https://cobbs-allen.sharefile.com/f/fodf52cc-2817-4922-bf95-8473a458c56a', '_blank');
      break;
  }
}

// ----------
// lifecycle
// ----------

onMounted(() => {
  loadRecentNavigation();
});
</script>

<template>
  <UserFeedbackModal
    :show="showUserFeedbackModal"
    @close="showUserFeedbackModal = false" />
  <div class="relative h-full bg-gray-50">
    <div
      v-if="!isCurrentHomepage"
      class="absolute top-2 right-2 z-10">
      <AppButton
        :loading="settingHomepage"
        @click="setAsHomepage">
        Set as Homepage
      </AppButton>
    </div>
    <!-- Main Content -->
    <main class="mx-auto max-w-7xl px-4 py-1 pt-4 sm:px-6 lg:px-4">
      <!-- Universal Search -->
      <div class="mb-4">
        <UniversalSearch />
      </div>

      <!-- Category Sections -->
      <div class="flex h-full flex-col gap-2">
        <!-- Quick Navigation Row -->
        <div
          v-if="hasRecentNavigation"
          class="flex flex-col gap-2 sm:mr-auto sm:ml-14 sm:flex-row">
          <div class="flex items-center text-xs font-medium text-gray-500">
            <History
              :size="17"
              class="mr-1" />
            Recent:
          </div>
          <div class="flex flex-wrap gap-2">
            <template
              v-for="link in recentNavigation"
              :key="link.to">
              <button
                class="flex items-center rounded-lg border border-solid border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-xs transition-all duration-150 hover:bg-gray-50"
                @click="router.push(link.to!)">
                <div class="mr-2 shrink-0 self-start">
                  <component
                    :is="iconMap[link.icon]"
                    class="h-4 w-4" />
                </div>
                {{ link.title }}
              </button>
            </template>
          </div>
        </div>
        <div v-else></div>

        <!-- All Cards Grid -->
        <div class="flex flex-col gap-5">
          <div class="flex flex-wrap justify-center gap-5">
            <template
              v-for="group in navigationGroups"
              :key="group.groupId">
              <!-- Category Card -->
              <div
                :style="{ borderColor: group.color + '45' }"
                class="flex w-full flex-col overflow-hidden rounded-lg border bg-white shadow-md transition-all duration-150 sm:w-[23rem]">
                <!-- Category Header -->
                <div class="flex shrink-0 items-center border-b border-gray-100 p-3">
                  <div class="mr-2 shrink-0 self-start pt-1">
                    <component
                      :is="group.icon"
                      :style="{ color: group.iconColor }"
                      class="h-4 w-4" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <h3 class="truncate text-sm font-semibold text-gray-900">{{ group.name }}</h3>
                    <p class="mt-0.5 h-8 text-xs leading-4 text-gray-500">{{ group.description }}</p>
                  </div>
                </div>

                <!-- Links List -->
                <div class="flex-1 divide-y divide-gray-100 overflow-y-auto">
                  <template
                    v-for="link in group.links"
                    :key="link.title">
                    <button
                      v-if="!link.hidden"
                      :style="{ '--hover-color': group.hoverColor }"
                      class="flex w-full cursor-pointer items-center border-none bg-transparent px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
                      @click="handleNavigation(link)">
                      <div class="mr-2 shrink-0 self-start">
                        <component :is="link.icon" :size="18" class="text-gray-500" />
                      </div>

                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-1.5">
                          <div class="truncate text-xs font-medium text-gray-700">{{ link.title }}</div>
                          <span
                            v-if="link.badge"
                            class="inline-flex shrink-0 items-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm">
                            {{ link.badge }}
                          </span>
                        </div>
                      </div>
                    </button>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
